import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import useAuth from "../customHooks/useAuth";

const SiteFilterContext = createContext();

const STORAGE_PREFIX = "siteFilter";
const DEFAULT_LOCATION_ID = 38;
const DEFAULT_SITE_ID = 46;

const getStorageKey = (userId) =>
  userId ? `${STORAGE_PREFIX}:${userId}` : `${STORAGE_PREFIX}:guest`;

export const SiteFilterProvider = ({ children }) => {
  const { auth } = useAuth();
  const userId = auth?.user_id ?? auth?.id ?? null;

  const [selectedLocationId, setSelectedLocationId] = useState(DEFAULT_LOCATION_ID);
  const [selectedSiteId, setSelectedSiteId] = useState(DEFAULT_SITE_ID);
  // Guards the persist effect so it never fires before rehydration is done,
  // which would overwrite sessionStorage with the initial default values.
  const [hydrated, setHydrated] = useState(false);

  // Rehydrate from sessionStorage on mount / user change
  useEffect(() => {
    const key = getStorageKey(userId);
    const stored = sessionStorage.getItem(key);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // Fall back to defaults so a stored null never wipes them out
        setSelectedLocationId(parsed.selectedLocationId ?? DEFAULT_LOCATION_ID);
        setSelectedSiteId(parsed.selectedSiteId ?? DEFAULT_SITE_ID);
      } catch {
        // ignore corrupt data
      }
    }
    setHydrated(true);
  }, [userId]);

  // Persist to sessionStorage only after the initial rehydration is complete
  useEffect(() => {
    if (!hydrated) return;
    const key = getStorageKey(userId);
    sessionStorage.setItem(
      key,
      JSON.stringify({ selectedLocationId, selectedSiteId })
    );
  }, [selectedLocationId, selectedSiteId, userId, hydrated]);

  const handleLocationChange = (locationId) => {
    setSelectedLocationId(locationId ?? null);
    setSelectedSiteId(null);
  };

  const handleSiteChange = (siteId) => {
    setSelectedSiteId(siteId ?? null);
  };

  const clearFilters = () => {
    setSelectedLocationId(null);
    setSelectedSiteId(null);
  };

  const value = useMemo(
    () => ({
      selectedLocationId,
      selectedSiteId,
      handleLocationChange,
      handleSiteChange,
      clearFilters,
    }),
    [selectedLocationId, selectedSiteId]
  );

  return (
    <SiteFilterContext.Provider value={value}>
      {children}
    </SiteFilterContext.Provider>
  );
};

export const useSiteFilterContext = () => useContext(SiteFilterContext);
